import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomHeaders } from '@shared/custom-headers';
import { HttpStatusCodes, HttpStatusCodesHelper } from '@shared/http-status-codes';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../../notification/notification.service';
import { InterceptorMethods } from './interceptor-methods';

import * as Sentry from '@sentry/angular-ivy';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(private notification: NotificationService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!InterceptorMethods.shouldIntercept(request)) {
            return next.handle(request);
        }

        return next.handle(request)
            .pipe(
                catchError(error => this.handleHttpError(error, request))
            );
    }

    private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<any> {
        let statusCode = HttpStatusCodesHelper.fetchFromHttpErrorResponse(error);

        ErrorInterceptor.captureSentryException(request, error);

        if (statusCode == HttpStatusCodes.NetworkError) {
            if ((error as any).retryExhausted) {
                this.notification.showNetworkError();
                return EMPTY;
            }
            return throwError(() => error); 
        }
        else if (statusCode == HttpStatusCodes.Unauthorized && error.headers.has(CustomHeaders.TokenExpired)) {
            // If the refresh token expired, don't log the error
            return throwError(error);
        }
        else if (statusCode == HttpStatusCodes.BadRequest && error.headers.has(CustomHeaders.InvalidTimestamp)) {
            // If the timestamp is invalid, don't log the error
            return throwError(error);
        }
        else {
            ErrorInterceptor.logHttpError(request, error);
            return throwError(error);
        }
    }

    private static logHttpError(request: HttpRequest<any>, error: HttpErrorResponse) {
        let requestName = InterceptorMethods.getRequestName(request);
        console.error(`HTTP Error. For request: ${requestName}. StatusCode: ${error && error.status}. ErrorResponse:`);
        console.error(error);
    }

    private static captureSentryException(request: HttpRequest<any>, error: HttpErrorResponse) {
        if (!(error instanceof HttpErrorResponse)) {
            return;
        }

        // Expected cases are intentionally skipped — normal auth/timing flows, not bugs.
        const isExpectedCase =
            (error.status === HttpStatusCodes.Unauthorized && error.headers.has(CustomHeaders.TokenExpired)) ||
            (error.status === HttpStatusCodes.BadRequest && error.headers.has(CustomHeaders.InvalidTimestamp));

        if (isExpectedCase) {
            return;
        }

        // Backend business errors (ShowMessage, e.g. "cannot delete, still referenced") are expected,
        // user-facing messages — report at warning level + tag so they stay filterable and don't
        // drown out real bugs. Everything else (5xx, 404, network, unexpected 4xx) stays error level.
        const isBusinessError = error.error?.ShowMessage === true;

        Sentry.withScope((scope) => {
            scope.setFingerprint([
                '{{ default }}',
                String(error.status),
                request.method,
                request.url,
            ]);

            scope.setTag('origin', 'http-interceptor');
            scope.setTag('http.status_code', String(error.status));
            scope.setExtra('request_url', request.urlWithParams);
            scope.setExtra('method', request.method);
            scope.setExtra('response_body', error.error);
            scope.setExtra('headers', error.headers);
            scope.setExtra('status_text', error.statusText);

            if (isBusinessError) {
                scope.setLevel('warning');
                scope.setTag('type', 'business_error');
            }

            Sentry.captureException(
                new Error(`HTTP ${error.status} for ${request.method} ${request.url}`),
            );
        });
    }
}
